import {
  Briefcase,
  ChevronDown,
  FileText,
  FolderOpen,
  Menu,
  MessageSquare,
  PanelLeftClose,
  Plus,
  Search,
  Send,
  Settings,
  Users,
  X,
  File,
  Layout,
  Bookmark,
} from "lucide-react";
import { useState, useRef } from "react";

const TASK_CARDS = [
  {
    icon: FileText,
    label: "Draft a CRA reassessment objection response",
    description: "CRA notice response",
  },
  {
    icon: Search,
    label: "Explain this CRA notice with sources",
    description: "CRA notice analysis",
  },
  {
    icon: FolderOpen,
    label: "Prepare a client document request email",
    description: "Client document request",
  },
  {
    icon: FileText,
    label: "Check a T4 slip before filing",
    description: "T4 slip review",
  },
  {
    icon: Search,
    label: "Start from a template",
    description: "Template selection",
  },
];

const RECENT_CHATS = [
  { title: "T1 review - Smith Family Trust" },
  { title: "Corporate reorganization" },
  { title: "GST/HST quarterlies" },
];

const NAV_ITEMS = [
  { icon: MessageSquare, label: "Chats" },
  { icon: File, label: "Documents" },
  { icon: FolderOpen, label: "Working Folder", count: 0 },
  { icon: Search, label: "Sources", count: 0 },
  { icon: Bookmark, label: "Templates" },
  { icon: Users, label: "Clients" },
  { icon: Settings, label: "Settings" },
];

const WF_TABS = ["All", "Drafts 0", "For review 0", "Final 0"];

const SOURCE_TABS = ["All", "Income Tax Act", "CRA", "Cases", "Other"];

export function G72BasePrototype() {
  const [text, setText] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const leftRail = (
    <aside
      data-testid="workspace-nav"
      className="flex flex-col shrink-0 w-[260px] bg-[#0f172a] text-white h-full"
    >
      <div className="flex items-center justify-between h-12 px-4 shrink-0 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-[#3b82f6] flex items-center justify-center text-sm font-bold">
            O
          </div>
          <span className="text-sm font-semibold tracking-tight">Orca</span>
        </div>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-white/10 transition-colors"
          onClick={() => setMobileNavOpen(false)}
        >
          <PanelLeftClose className="w-4 h-4 text-white/60" />
        </button>
      </div>

      <div className="px-3 pt-3 shrink-0">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg bg-white/10 text-sm font-medium hover:bg-white/20 transition-colors"
        >
          <div className="w-6 h-6 rounded bg-[#22c55e]/20 flex items-center justify-center text-sm text-[#22c55e] font-bold">
            S
          </div>
          <span className="flex-1 text-left">Spruce</span>
          <ChevronDown className="w-4 h-4 text-white/50" />
        </button>
      </div>

      <div className="px-3 pt-3 shrink-0">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New chat
        </button>
      </div>

      <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40 px-4 pt-4 pb-1.5">
        Workspaces
      </p>

      <nav className="flex flex-col gap-0.5 px-2 shrink-0">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.label}
            type="button"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors text-left w-full"
          >
            <item.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.count !== undefined && (
              <span className="text-xs text-white/40">{item.count}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="flex flex-col min-h-0 flex-1 px-2 pt-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40 px-3 pb-1.5">
          Recent Chats
        </p>
        <div className="overflow-y-auto flex-1 min-h-0 hide-scrollbar">
          <div className="flex flex-col gap-0.5">
            {RECENT_CHATS.map((chat) => (
              <button
                key={chat.title}
                type="button"
                className="w-full text-left px-3 py-2 rounded text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors truncate"
              >
                {chat.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="shrink-0 px-3 pb-3 pt-2 border-t border-white/10">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-full bg-[#3b82f6]/30 flex items-center justify-center text-sm text-[#3b82f6] font-medium">
            RC
          </div>
          <span className="text-sm text-white/70 truncate">rcarlson@csaicpa.ca</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-white" data-testid="g72-base-prototype">
      {/* Desktop left rail */}
      <div className="hidden lg:flex shrink-0">{leftRail}</div>

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 z-50 shadow-xl">
            {leftRail}
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0 overflow-x-hidden bg-[#f8fafc]">
        <div className="h-14 flex items-center justify-between px-4 lg:px-6 shrink-0 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="lg:hidden flex items-center justify-center w-8 h-8 rounded hover:bg-[#f1f5f9] transition-colors"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="w-5 h-5 text-[#475569]" />
            </button>
            <span className="text-sm font-medium text-[#0f172a]">Spruce</span>
          </div>
          <div className="text-xs text-[#64748b]">Orca CPA Workbench</div>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto hide-scrollbar"
          data-testid="prototype-center-pane"
        >
          <div className="flex flex-col items-center justify-center min-h-full gap-6 sm:gap-8 px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-6 sm:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#3b82f6]/10 flex items-center justify-center shrink-0">
              <Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-[#3b82f6]" />
            </div>

            <div className="text-center">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0f172a] mb-2 sm:mb-3">
                What CPA work should Orca handle?
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-[#64748b] max-w-lg mx-auto">
                Choose a task to get started, or describe the work you need done.
              </p>
            </div>

            <div
              className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
              data-testid="prototype-task-cards"
            >
              {TASK_CARDS.map((card) => (
                <button
                  key={card.label}
                  type="button"
                  className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2 w-full px-3 py-3 sm:px-4 sm:py-4 rounded-xl border border-[#e2e8f0] bg-white hover:border-[#3b82f6] hover:shadow-md transition-all text-left"
                >
                  <card.icon className="w-5 h-5 text-[#64748b] shrink-0 sm:mb-1" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm sm:text-base font-medium text-[#0f172a] leading-snug">
                      {card.label}
                    </p>
                    <p className="text-xs sm:text-sm text-[#64748b] leading-tight hidden sm:block mt-1">
                      {card.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 px-4 lg:px-8 py-4 pb-6 sm:pb-4" data-testid="composer" onClick={() => textareaRef.current?.focus()}>
          <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe a tax, bookkeeping, or compliance task..."
              className="w-full px-4 sm:px-5 py-3 sm:py-4 text-base text-[#0f172a] placeholder-[#94a3b8] bg-transparent border-none outline-none resize-none font-sans"
              style={{ minHeight: "56px", maxHeight: "180px" }}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                }
              }}
            />
            <div className="flex items-center justify-between px-4 sm:px-5 pb-3 sm:pb-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 rounded hover:bg-[#f1f5f9] transition-colors"
                >
                  <Plus className="w-4 h-4 text-[#64748b]" />
                </button>
              </div>
              <button
                type="button"
                disabled={!text.trim()}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-lg px-5 py-2 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>
          <p className="text-xs text-center text-[#94a3b8] mt-3">
            Claude can make mistakes. Please double-check responses.
          </p>
        </div>
      </main>

      <aside
        className="hidden lg:flex flex-col shrink-0 w-[320px] border-l border-[#e2e8f0] bg-white p-4 gap-4"
        data-testid="prototype-right-pane"
      >
        <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#e2e8f0]">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#64748b]" />
              <span className="text-sm font-semibold text-[#0f172a]">Working Folder</span>
              <span className="text-xs text-[#94a3b8]">0</span>
            </div>
            <button
              type="button"
              className="text-xs text-[#3b82f6] hover:text-[#2563eb] font-medium"
            >
              Open
            </button>
          </div>
          <div className="flex gap-1 px-3 pt-3 pb-2 border-b border-[#e2e8f0] overflow-x-auto">
            {WF_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  tab === "All"
                    ? "bg-[#0f172a] text-white"
                    : "text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="px-4 py-8 flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-[#94a3b8]" />
            </div>
            <p className="text-sm text-[#64748b] text-center">No files yet</p>
            <p className="text-xs text-[#94a3b8] text-center max-w-[200px]">
              Upload client documents or generate workpapers to see them here.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden flex-1 min-h-0">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#e2e8f0]">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#64748b]" />
              <span className="text-sm font-semibold text-[#0f172a]">Sources</span>
              <span className="text-xs text-[#94a3b8]">0</span>
            </div>
            <button
              type="button"
              className="text-xs text-[#3b82f6] hover:text-[#2563eb] font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex gap-0.5 px-2 pt-3 pb-2 border-b border-[#e2e8f0] overflow-x-auto min-w-0" data-testid="sources-tabs">
            {SOURCE_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`px-1.5 py-1 text-[11px] font-medium rounded transition-colors whitespace-nowrap shrink-0 ${
                  tab === "All"
                    ? "bg-[#0f172a] text-white"
                    : "text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="px-4 py-8 flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center">
              <Search className="w-5 h-5 text-[#94a3b8]" />
            </div>
            <p className="text-sm text-[#64748b] text-center">No sources selected</p>
            <p className="text-xs text-[#94a3b8] text-center max-w-[200px]">
              Ask Orca a question to retrieve relevant Canadian tax sources.
            </p>
          </div>
          <div className="px-4 pb-3">
            <p className="text-xs text-[#94a3b8] leading-relaxed">
              Sources include Canadian tax legislation, CRA guidance, and case law.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}
